// Projects and products: the list, a project's page, the new-project dialog, and a client's products.
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { Icon, Modal, ago, useConfirm, useToast } from '../ui';
import type { Client, Product, Project, ProjectState, ProjectSummary } from '../types';
import { PROJECT_STATE_BADGE, PROJECT_STATE_LABELS } from '../types';
import { Page, useCrumbs } from './Shell';
import { RoundsSection, VISIBILITY_LABELS } from './Designs';
import { BriefSection } from './Brief';
import { ReviewLinksSection } from './Review';
import { SpecHandoffSection } from './Spec';
import { NextRoundSection } from './Ideas';
import { ClientQuestionsSection, QuestionsPanel } from './Questions';
import { TicketsTab } from './Tickets';
import { ProjectProgress } from './Progress';

const STATES = Object.keys(PROJECT_STATE_LABELS) as ProjectState[];
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 70);

function useProjectTopics() {
  const { me } = useAuth();
  useLiveTopics(me?.is_staff ? 'workspace' : me?.client ? `client:${me.client.id}` : null);
}

export const StateBadge = ({ state }: { state: ProjectState }) => <span className={`badge ${PROJECT_STATE_BADGE[state]}`}>{PROJECT_STATE_LABELS[state]}</span>;

// ---------- list ----------

export function ProjectList() {
  const { me, can } = useAuth();
  const setCrumbs = useCrumbs();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<ProjectState | ''>('');
  const [creating, setCreating] = useState(false);
  useProjectTopics();
  useEffect(() => setCrumbs([{ label: me?.is_staff ? 'Projects' : me?.client?.name ?? 'Projects' }]), [me, setCrumbs]);
  const { data: projects = [], isLoading } = useQuery({ queryKey: ['projects', 'all'], queryFn: () => get<Project[]>('/api/projects') });
  const shown = projects.filter((p) => (!state || p.state === state)
    && `${p.name} ${p.client.name} ${p.products.map((x) => x.name).join(' ')}`.toLowerCase().includes(query.toLowerCase()));

  if (!me) return null;
  if (!me.is_staff) {
    return (
      <Page title={`Welcome, ${me.name.split(' ')[0]}`} eyebrow={me.client?.name}>
        {!isLoading && !projects.length && <div className="card empty">Your projects will appear here once Blue Market shares them with you.</div>}
        <div className="project-cards">
          {projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="card pad project-card">
              <div className="row" style={{ justifyContent: 'space-between' }}><b>{p.name}</b><StateBadge state={p.state} /></div>
              {p.summary && <p className="muted small clamp-3" style={{ margin: 0 }}>{p.summary}</p>}
              {p.products.length > 0 && <div className="tiny muted"><Icon name="box" className="ico sm" /> {p.products.map((x) => x.name).join(', ')}</div>}
            </Link>
          ))}
        </div>
      </Page>
    );
  }
  return (
    <Page title="Projects" eyebrow="Workspace" actions={can('delivery.edit') && <button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" />New project</button>}>
      <div className="row wrap" style={{ marginBottom: 14 }}>
        <label className="row input" style={{ maxWidth: 320, padding: '0 10px', gap: 6 }}>
          <Icon name="search" />
          <input aria-label="Search projects" placeholder="Search projects, clients, products" value={query} onChange={(e) => setQuery(e.target.value)} style={{ border: 0, outline: 0, background: 'none', flex: 1, minWidth: 0, height: 36 }} />
        </label>
        <select className="input" style={{ width: 'auto' }} value={state} onChange={(e) => setState(e.target.value as ProjectState | '')} aria-label="Filter by state">
          <option value="">All states</option>
          {STATES.map((s) => <option key={s} value={s}>{PROJECT_STATE_LABELS[s]}</option>)}
        </select>
      </div>
      <ProjectTable projects={shown} showClient empty={isLoading ? '' : projects.length ? 'No projects match.' : 'No projects yet.'} />
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
    </Page>
  );
}

export function ProjectTable({ projects, showClient, empty }: { projects: Project[]; showClient?: boolean; empty: string }) {
  const navigate = useNavigate();
  return (
    <div className="card table-wrap">
      <table className="table">
        <thead><tr><th>Project</th>{showClient && <th>Client</th>}<th>State</th><th>Products</th><th>Updated</th></tr></thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="clickable" onClick={() => navigate(`/projects/${p.id}`)}>
              <td>
                <Link to={`/projects/${p.id}`} onClick={(e) => e.stopPropagation()}><b>{p.name}</b></Link>
                {p.client_visible === false && <span className="badge" style={{ marginLeft: 8 }} title="Blue Market only">Hidden from client</span>}
              </td>
              {showClient && <td><Link to={`/clients/${p.client.id}?tab=projects`} onClick={(e) => e.stopPropagation()}>{p.client.name}</Link></td>}
              <td><StateBadge state={p.state} /></td>
              <td className="muted">{p.products.map((x) => x.name).join(', ') || '–'}</td>
              <td className="muted">{ago(p.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {empty && !projects.length && <div className="empty">{empty}</div>}
    </div>
  );
}

// ---------- new project ----------

export function NewProjectDialog({ clientId, onClose }: { clientId?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => get<Client[]>('/api/clients') });
  const [form, setForm] = useState({ client_id: clientId ?? '', name: '', slug: '', summary: '', state: 'discovery' as ProjectState, client_visible: true });
  const [slugTouched, setSlugTouched] = useState(false);
  const [productIds, setProductIds] = useState<string[]>([]);
  const { data: products = [] } = useQuery({
    queryKey: ['products', form.client_id], enabled: !!form.client_id,
    queryFn: () => get<Product[]>(`/api/clients/${form.client_id}/products`),
  });
  const client = clients.find((c) => c.id === form.client_id);
  const slug = slugTouched ? form.slug : slugify(form.name);
  const create = useMutation({
    mutationFn: () => post<Project>('/api/projects', { ...form, slug: slug || null, product_ids: productIds }),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ['projects'] }); toast('Project created'); navigate(`/projects/${p.id}`); },
    onError: (e: Error) => toast(e.message, true),
  });
  const current = products.filter((p) => !p.archived);
  return (
    <Modal title="New project" onClose={onClose} width={560}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
        {!clientId && (
          <label className="field"><span>Client</span>
            <select className="input" value={form.client_id} onChange={(e) => { setForm({ ...form, client_id: e.target.value }); setProductIds([]); }} required>
              <option value="" disabled>Choose a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Name</span><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} autoFocus placeholder="e.g. Loyalty app" /></label>
        <label className="field"><span>Folder</span>
          <div className="row" style={{ gap: 0 }}>
            <span className="input-prefix mono" title={`clients/${client?.slug ?? "…"}/`}>clients/{client?.slug ?? "…"}/</span>
            <input className="input mono" value={slug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?" maxLength={70} required aria-label="Folder name" />
          </div>
          <span className="hint">Where its designs, notes and spec are kept. Fixed once created; renaming the project doesn't move it.</span>
        </label>
        <label className="field"><span>Summary</span><textarea className="input" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} maxLength={5000} /></label>
        <div className="row wrap" style={{ alignItems: 'flex-start', gap: 16 }}>
          <label className="field"><span>State</span>
            <select className="input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value as ProjectState })}>
              {STATES.map((s) => <option key={s} value={s}>{PROJECT_STATE_LABELS[s]}</option>)}
            </select>
          </label>
          <label className="row small" style={{ marginTop: 28 }}><input type="checkbox" checked={form.client_visible} onChange={(e) => setForm({ ...form, client_visible: e.target.checked })} />Visible to the client's people</label>
        </div>
        {form.client_id && (
          <div className="field"><span>Products</span>
            {current.length ? <ProductPicker products={current} selected={productIds} onChange={setProductIds} />
              : <span className="hint">This client has no products yet. You can add them from the client's Projects tab and link them later.</span>}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={create.isPending || !form.client_id}>{create.isPending && <span className="spin" />}Create project</button>
        </div>
      </form>
    </Modal>
  );
}

function ProductPicker({ products, selected, onChange }: { products: Product[]; selected: string[]; onChange: (ids: string[]) => void }) {
  return (
    <div className="check-list">
      {products.map((p) => (
        <label key={p.id} className="row small">
          <input type="checkbox" checked={selected.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...selected, p.id] : selected.filter((x) => x !== p.id))} />
          <span><b>{p.name}</b>{p.description && <span className="muted"> · {p.description}</span>}</span>
        </label>
      ))}
    </div>
  );
}

// ---------- project page ----------

type Tab = 'overview' | 'tickets' | 'designs' | 'questions' | 'links' | 'spec' | 'settings';

export function ProjectDetail() {
  const { id = '' } = useParams();
  const { me, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  const [search, setSearch] = useSearchParams();
  useProjectTopics();
  const { data: project, error } = useQuery({ queryKey: ['project', id], queryFn: () => get<Project>(`/api/projects/${id}`), retry: false });
  const { data: summary } = useQuery({ queryKey: ['project-summary', id], queryFn: () => get<ProjectSummary>(`/api/projects/${id}/summary`), enabled: !!project });
  useEffect(() => {
    if (!project) return;
    setCrumbs(me?.is_staff
      ? [{ label: 'Clients', to: '/clients' }, { label: project.client.name, to: `/clients/${project.client.id}?tab=projects` }, { label: project.name }]
      : [{ label: project.client.name, to: '/' }, { label: project.name }]);
  }, [project, me, setCrumbs]);
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => patch<Project>(`/api/projects/${id}`, body),
    onSuccess: (p) => { qc.setQueryData(['project', id], p); qc.invalidateQueries({ queryKey: ['projects'] }); },
    onError: (e: Error) => toast(e.message, true),
  });

  if (error) return <Page title="Not found"><p className="muted">This project doesn't exist, or you don't have access to it.</p></Page>;
  if (!project) return <main className="page"><span className="spin muted" /></main>;
  const staff = !!me?.is_staff;
  const tabs: { key: Tab; label: string; count?: number; warn?: boolean }[] = [
    { key: 'overview', label: 'Overview', ...(!staff && summary?.client_questions?.open ? { count: summary.client_questions.open, warn: true } : {}) },
    ...(can('tickets.view') ? [{ key: 'tickets' as Tab, label: 'Tickets', count: summary?.tickets?.open }] : []),
    { key: 'designs', label: 'Designs', count: summary?.latest_round?.open_comments || undefined },
    ...(staff ? [
      { key: 'questions' as Tab, label: 'Questions', count: summary?.questions?.open || undefined },
      ...(can('designs.publish') ? [{ key: 'links' as Tab, label: 'Review links', count: summary?.review_links || undefined }] : []),
      { key: 'spec' as Tab, label: 'Spec & handoff' },
      { key: 'settings' as Tab, label: 'Settings' },
    ] : []),
  ];
  const tab: Tab = tabs.some((t) => t.key === search.get('tab')) ? search.get('tab') as Tab : 'overview';
  const go = (t: Tab) => setSearch(t === 'overview' ? {} : { tab: t });

  return (
    <Page title={project.name} eyebrow={staff ? project.client.name : 'Project'}
          actions={<>
            {project.archived && <span className="badge warn">Archived</span>}
            {project.client_visible === false && <span className="badge" title="Blue Market only">Hidden from client</span>}
            <StateBadge state={project.state} />
          </>}>
      <nav className="tabs project-tabs" aria-label="Project">
        {tabs.map((t) => (
          <a key={t.key} href={`?tab=${t.key}`} className={tab === t.key ? 'active' : ''} aria-current={tab === t.key ? 'page' : undefined}
             onClick={(e) => { e.preventDefault(); go(t.key); }}>
            {t.label}{!!t.count && <span className={`tab-count ${t.warn ? 'warn' : ''}`}>{t.count}</span>}
          </a>
        ))}
      </nav>
      {tab === 'overview' && <Overview project={project} summary={summary} go={go} />}
      {tab === 'tickets' && <TicketsTab project={project} />}
      {tab === 'designs' && <>{staff && <BriefSection project={project} />}<RoundsSection project={project} />{staff && <NextRoundSection project={project} />}</>}
      {tab === 'questions' && <QuestionsPanel project={project} />}
      {tab === 'links' && <ReviewLinksSection project={project} />}
      {tab === 'spec' && <SpecHandoffSection project={project} />}
      {tab === 'settings' && <ProjectSettings project={project} update={update.mutate} />}
    </Page>
  );
}

function Tile({ label, value, sub, onClick, tone }: { label: string; value: string | number; sub?: ReactNode; onClick: () => void; tone?: 'warn' | 'bad' }) {
  return (
    <button className={`tile ${tone ?? ''}`} onClick={onClick}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {sub && <span className="tile-sub">{sub}</span>}
    </button>
  );
}

function Overview({ project, summary, go }: { project: Project; summary: ProjectSummary | undefined; go: (t: Tab) => void }) {
  const { me, can } = useAuth();
  const staff = !!me?.is_staff;
  const s = summary;
  const r = s?.latest_round;
  return (
    <div className="stack" style={{ gap: 16 }}>
      {(project.summary || project.products.length > 0) && (
        <div className="overview-about">
          {project.summary && <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{project.summary}</p>}
          {project.products.length > 0 && <div className="row wrap" style={{ gap: 6, marginTop: project.summary ? 10 : 0 }}>
            {project.products.map((p) => <span key={p.id} className="badge"><Icon name="box" className="ico sm" />{p.name}</span>)}
          </div>}
        </div>
      )}
      {s && (
        <div className="tiles">
          {s.tickets && can('tickets.view') && <Tile label="Open tickets" value={s.tickets.open}
                                                    sub={<>{s.tickets.doing} in progress{s.tickets.overdue > 0 && <> · <span className="bad-text">{s.tickets.overdue} overdue</span></>}</>}
                                                    onClick={() => go('tickets')} />}
          <Tile label="Designs" value={r ? `v${r.number}` : '–'} sub={r ? [VISIBILITY_LABELS[r.visibility], r.open_comments ? `${r.open_comments} open comment${r.open_comments === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ') : 'No rounds yet'}
                tone={r?.open_comments ? 'warn' : undefined} onClick={() => go('designs')} />
          {staff && s.questions && <Tile label="Open questions" value={s.questions.open}
                                         sub={s.questions.asked ? `${s.questions.asked} asked of the client · ${s.questions.client_answered} answered` : 'None asked of the client'} onClick={() => go('questions')} />}
          {staff && s.ideas_open !== undefined && <Tile label="Next round" value={s.ideas_open} sub={`idea${s.ideas_open === 1 ? '' : 's'} waiting`} onClick={() => go('designs')} />}
        </div>
      )}
      {can('tickets.view') && <ProjectProgress project={project} />}
      {!staff && <ClientQuestionsSection project={project} />}
      {staff && !project.summary && <p className="muted small" style={{ margin: 0 }}>No summary yet. Add one in <a href="?tab=settings" onClick={(e) => { e.preventDefault(); go('settings'); }}>Settings</a>.</p>}
    </div>
  );
}

function ProjectSettings({ project, update }: { project: Project; update: (body: Record<string, unknown>) => void }) {
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [linking, setLinking] = useState(false);
  const { data: client } = useQuery({ queryKey: ['client', project.client.id], queryFn: () => get<Client>(`/api/clients/${project.client.id}`) });
  const edit = can('delivery.edit');
  const setVisible = async (v: boolean) => {
    if (!v && !(await confirm(`Hide ${project.name} from ${project.client.name}?`, { body: "Their people stop seeing it straight away, including its designs and tickets.", ok: 'Hide', danger: true }))) return;
    update({ client_visible: v });
  };
  const archive = async () => {
    if (!(await confirm(`Archive ${project.name}?`, { body: 'It leaves the project lists and the client stops seeing it. Its files stay where they are, and you can restore it later.', ok: 'Archive', danger: true }))) return;
    update({ archived: true });
  };
  return (
    <>
      <div className="grid-2">
        <section className="card pad stack">
          <h2>About</h2>
          <label className="field"><span>Name</span>
            <input className="input" defaultValue={project.name} key={`n${project.updated_at}`} maxLength={200} required disabled={!edit} onBlur={(e) => e.target.value.trim() && e.target.value !== project.name && update({ name: e.target.value })} />
          </label>
          <label className="field"><span>State</span>
            <select className="input" value={project.state} disabled={!edit} onChange={(e) => update({ state: e.target.value })}>
              {STATES.map((s) => <option key={s} value={s}>{PROJECT_STATE_LABELS[s]}</option>)}
            </select>
          </label>
          <label className="field"><span>Summary</span>
            <textarea className="input" rows={4} defaultValue={project.summary} key={`s${project.updated_at}`} maxLength={5000} disabled={!edit} onBlur={(e) => e.target.value !== project.summary && update({ summary: e.target.value })} />
          </label>
        </section>
        <section className="card pad stack">
          <h2>Client access</h2>
          <label className="row small" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={!!project.client_visible} disabled={!edit} onChange={(e) => setVisible(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>Visible to {project.client.name}</b><br /><span className="muted">Their people see this project, designs marked ready for comment, and its tickets.</span></span>
          </label>
          <label className="field"><span>Can their people prioritise and move tickets?</span>
            <select className="input" disabled={!edit} value={project.client_can_triage === null || project.client_can_triage === undefined ? 'default' : project.client_can_triage ? 'yes' : 'no'}
                    onChange={(e) => update({ client_can_triage: e.target.value === 'default' ? null : e.target.value === 'yes' })}>
              <option value="default">Client default ({client ? (client.client_can_triage ? 'yes' : 'no') : '…'})</option>
              <option value="yes">Yes, for this project</option>
              <option value="no">No, for this project</option>
            </select>
            <span className="hint">They can always create tickets and comment. This also lets them set priority, reorder and change statuses.</span>
          </label>
        </section>
        <section className="card pad stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2>Products</h2>
            {edit && <button className="btn sm" onClick={() => setLinking(true)}><Icon name="edit" />Change</button>}
          </div>
          <div className="row wrap" style={{ gap: 6 }}>
            {project.products.map((p) => <span key={p.id} className="badge" style={{ height: 28 }}><Icon name="box" className="ico sm" />{p.name}</span>)}
            {!project.products.length && <span className="muted small">Not linked to a product.</span>}
          </div>
          <h2 style={{ marginTop: 8 }}>Files</h2>
          <div className="row small"><Icon name="folder" /><span className="mono">{project.path}</span></div>
        </section>
      </div>
      {edit && (
        <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
          {project.archived
            ? <button className="btn" onClick={() => update({ archived: false })}>Restore project</button>
            : <button className="btn ghost danger" onClick={archive}>Archive project</button>}
        </div>
      )}
      {linking && <LinkProducts project={project} onClose={() => setLinking(false)} />}
      {dialog}
    </>
  );
}

function LinkProducts({ project, onClose }: { project: Project; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(project.products.map((p) => p.id));
  const [name, setName] = useState('');
  const { data: products = [] } = useQuery({ queryKey: ['products', project.client.id], queryFn: () => get<Product[]>(`/api/clients/${project.client.id}/products`) });
  const current = products.filter((p) => !p.archived);
  const save = useMutation({
    mutationFn: () => put<Project>(`/api/projects/${project.id}/products`, { product_ids: selected }),
    onSuccess: (p) => { qc.setQueryData(['project', project.id], p); qc.invalidateQueries({ queryKey: ['projects'] }); toast('Products updated'); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  const add = useMutation({
    mutationFn: () => post<Product>(`/api/clients/${project.client.id}/products`, { name }),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ['products', project.client.id] }); setSelected((s) => [...s, p.id]); setName(''); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`Products for ${project.name}`} onClose={onClose} width={480}>
      <p className="muted small" style={{ margin: 0 }}>A project can belong to several of {project.client.name}'s products. Change this any time.</p>
      {current.length ? <ProductPicker products={current} selected={selected} onChange={setSelected} /> : <p className="muted small" style={{ margin: 0 }}>No products yet.</p>}
      <form className="row" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
        <input className="input" placeholder="New product, e.g. Vet portal" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} aria-label="New product name" />
        <button className="btn" disabled={!name.trim() || add.isPending}>Add</button>
      </form>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>Save</button>
      </div>
    </Modal>
  );
}

// ---------- a client's projects tab ----------

export function ClientProjects({ client }: { client: Client }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', client.id, showArchived],
    queryFn: () => get<Project[]>(`/api/projects?client_id=${client.id}${showArchived ? '&include_archived=true' : ''}`),
  });
  const setDefault = useMutation({
    mutationFn: (v: boolean) => patch<Client>(`/api/clients/${client.id}`, { client_can_triage: v }),
    onSuccess: (c) => { qc.setQueryData(['client', client.id], c); qc.invalidateQueries({ queryKey: ['projects'] }); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row wrap" style={{ justifyContent: 'space-between' }}>
        <label className="row small"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />Show archived</label>
        {can('delivery.edit') && <button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" />New project</button>}
      </div>
      <ProjectTable projects={projects} empty={isLoading ? '' : `No projects for ${client.name} yet.`} />
      <div className="grid-2">
        <ClientProducts client={client} />
        <section className="card pad stack">
          <h2>Project defaults</h2>
          <label className="row small" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={client.client_can_triage} disabled={!can('clients.edit')} onChange={(e) => setDefault.mutate(e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>{client.name}'s people can prioritise and move tickets</b><br /><span className="muted">Applies to projects set to follow the client default. Each project can override it.</span></span>
          </label>
        </section>
      </div>
      {creating && <NewProjectDialog clientId={client.id} onClose={() => setCreating(false)} />}
    </div>
  );
}

function ClientProducts({ client }: { client: Client }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<Product | null>(null);
  const { data: products = [] } = useQuery({ queryKey: ['products', client.id], queryFn: () => get<Product[]>(`/api/clients/${client.id}/products`) });
  const sorted = useMemo(() => [...products].sort((a, b) => Number(a.archived) - Number(b.archived)), [products]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['products', client.id] }); qc.invalidateQueries({ queryKey: ['projects'] }); };
  const add = useMutation({
    mutationFn: () => post<Product>(`/api/clients/${client.id}/products`, { name }),
    onSuccess: () => { refresh(); setName(''); },
    onError: (e: Error) => toast(e.message, true),
  });
  const save = async (p: Product, body: Partial<Product>) => {
    try { await patch(`/api/products/${p.id}`, body); refresh(); setEditing(null); } catch (e) { toast((e as Error).message, true); }
  };
  const edit = can('delivery.edit');
  return (
    <section className="card pad stack">
      <div><h2>Products</h2><p className="muted small" style={{ margin: '4px 0 0' }}>What {client.name} owns, e.g. an app or a portal. Projects link to one or more.</p></div>
      <div>
        {sorted.map((p) => (
          <div key={p.id} className="device" style={p.archived ? { opacity: .55 } : undefined}>
            <Icon name="box" />
            <div className="grow"><b>{p.name}</b>{p.archived && <span className="muted small"> (archived)</span>}{p.description && <div className="muted small">{p.description}</div>}</div>
            {edit && <button className="btn sm ghost" onClick={() => setEditing(p)}>Edit</button>}
          </div>
        ))}
        {!products.length && <span className="muted small">None yet.</span>}
      </div>
      {edit && (
        <form className="row" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <input className="input" placeholder="e.g. Rewards app" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} aria-label="New product name" />
          <button className="btn" disabled={!name.trim() || add.isPending}>Add</button>
        </form>
      )}
      {editing && <EditProduct product={editing} onSave={(b) => save(editing, b)} onClose={() => setEditing(null)} />}
    </section>
  );
}

function EditProduct({ product, onSave, onClose }: { product: Product; onSave: (b: Partial<Product>) => void; onClose: () => void }) {
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  return (
    <Modal title="Edit product" onClose={onClose} width={460}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); onSave({ name, description }); }}>
        <label className="field"><span>Name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoFocus /></label>
        <label className="field"><span>Description</span><textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} /></label>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className={`btn ghost ${product.archived ? '' : 'danger'}`} onClick={() => onSave({ archived: !product.archived })}>{product.archived ? 'Restore' : 'Archive'}</button>
          <div className="row">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary">Save</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
